import { Box } from '@mui/material';
import { mappedContractId } from '@timelog/pm-shared';
import type { PlanNode } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { stackAllocations, withAppendedAllocation } from '../allocationOps';
import { usePlanEditor } from '../editor';
import { roleCatalogNotice } from '../personRole';
import { ResourcesPopover } from '../ResourcesPopover';
import { useRoleOptions } from '../useRoleOptions';

import { RolePickerPopover } from './RolePickerPopover';
import { TIMELINE_ROW_HEIGHT_PX } from './timelineGeometry';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { roleColor } from '@/lib/roleColor';

/**
 * The bar's staffing readout (v5, Area 2/3): the allocation stack just past
 * the bar's right edge — persons as role-tinted avatars, unresolved roles as
 * dashed chips — plus a hover-revealed add affordance. Every control stops
 * propagation on pointerdown as well as click, or it would start a bar drag
 * (the timeline binds drags on pointerdown, not click).
 *
 * Three gestures, each the shortest path to what it does. `+` runs the
 * role-first picker; a dashed chip is a role still missing a person, so it
 * assigns one in a click. An avatar (or the `+n` overflow) opens the LIST's
 * Resources popover — the full allocation table, role, person and hours per
 * line — because changing an existing allocation is the one thing the
 * two-step picker cannot do, and the list already has the editor for it.
 */

const CHIP_TOP_PX = (TIMELINE_ROW_HEIGHT_PX - 18) / 2;

const stopPointer = (event: ReactPointerEvent | MouseEvent) => {
  event.stopPropagation();
};

export type AllocationStackProps = {
  node: PlanNode;
  /** Track-relative x where the stack starts (bar right edge + margin). */
  leftPx: number;
};

export const AllocationStack = ({ node, leftPx }: AllocationStackProps) => {
  const { t } = useTranslation('pmPlanning');
  const { t: tPanel } = useTranslation('pmPlanningPanel');
  const { dispatch } = usePlanEditor();
  // State, not a ref: the popover anchor must be readable during render.
  const [stackEl, setStackEl] = useState<HTMLElement | null>(null);
  const contractId = mappedContractId(node);
  const { roleOptions, pricing, lookupsEnabled, catalogFailed, source, legalEntityName } =
    useRoleOptions(contractId);
  const catalogNotice = roleCatalogNotice(
    {
      optionCount: roleOptions.length,
      lookupsEnabled,
      failed: catalogFailed,
      source,
      legalEntityName,
    },
    tPanel,
  );
  const [picker, setPicker] = useState<
    | { anchorEl: HTMLElement; mode: 'add' }
    | { anchorEl: HTMLElement; mode: 'assign'; allocationId: string }
    | null
  >(null);

  const { shown, overflow } = stackAllocations(node.allocations);
  const assignTarget =
    picker?.mode === 'assign'
      ? node.allocations.find((allocation) => allocation.id === picker.allocationId)
      : undefined;

  const setAllocations = (allocations: PlanNode['allocations']) =>
    dispatch({ type: 'SET_ALLOCATIONS', nodeId: node.id, allocations });

  const openResources = (event?: MouseEvent) => {
    event?.stopPropagation();
    setPicker(null);
    dispatch({ type: 'OPEN_RESOURCES', id: node.id });
  };

  return (
    <>
      <Box
        ref={setStackEl}
        data-automation-id="timeline-allocation-stack"
        onPointerDown={stopPointer}
        onMouseDown={stopPointer}
        onClick={stopPointer}
        sx={{
          position: 'absolute',
          top: `${String(CHIP_TOP_PX)}px`,
          left: `${String(leftPx)}px`,
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          zIndex: 2,
        }}
      >
        {shown.map((allocation) =>
          allocation.personRef ? (
            <Box
              key={allocation.id}
              component="button"
              type="button"
              title={t('timeline.AvatarTitle', {
                person: allocation.personRef.snapshot.name,
                role: allocation.roleRef.snapshot.name,
              })}
              data-automation-id="timeline-allocation-avatar"
              onClick={openResources}
              sx={{
                width: 18,
                height: 18,
                p: 0,
                borderRadius: '50%',
                bgcolor: roleColor(allocation.roleRef.tlpId),
                color: 'var(--color-black-00)',
                fontFamily: 'var(--font-family)',
                fontSize: 10,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'var(--border-width-thick) solid var(--bg-panel)',
                cursor: 'pointer',
                ml: '-4px',
                '&:first-of-type': { ml: 0 },
                '&:hover': { borderColor: 'var(--color-purple-80)' },
              }}
            >
              {allocation.personRef.snapshot.name.charAt(0)}
            </Box>
          ) : (
            <Box
              key={allocation.id}
              component="button"
              type="button"
              title={t('timeline.RoleChipTitle', {
                role: allocation.roleRef.snapshot.name.toLowerCase(),
              })}
              data-automation-id="timeline-allocation-role-chip"
              onClick={(event) => {
                event.stopPropagation();
                setPicker({
                  anchorEl: event.currentTarget as HTMLElement,
                  mode: 'assign',
                  allocationId: allocation.id,
                });
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 18,
                pl: '6px',
                pr: '7px',
                border: `var(--border-width-thin) dashed ${roleColor(allocation.roleRef.tlpId)}`,
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: 'var(--bg-panel)',
                fontSize: 10,
                fontFamily: 'var(--font-family)',
                color: 'var(--fg-2)',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                '&:hover': { borderStyle: 'solid', color: 'var(--fg-1)' },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: roleColor(allocation.roleRef.tlpId),
                  opacity: 0.7,
                  flex: 'none',
                }}
              />
              {allocation.roleRef.snapshot.name}
            </Box>
          ),
        )}
        {overflow > 0 ? (
          <Box
            component="button"
            type="button"
            title={t('timeline.OverflowTitle', { count: node.allocations.length })}
            data-automation-id="timeline-allocation-overflow"
            onClick={openResources}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 18,
              px: '6px',
              borderRadius: 'var(--border-radius-pill)',
              border: 'var(--border-width-thin) solid var(--border-divider)',
              bgcolor: 'var(--bg-sunken)',
              fontFamily: 'var(--font-family-mono)',
              fontSize: 10,
              color: 'var(--fg-2)',
              cursor: 'pointer',
              '&:hover': { borderColor: 'var(--color-purple-80)', color: 'var(--fg-1)' },
            }}
          >
            +{overflow}
          </Box>
        ) : null}
        <Box
          component="button"
          type="button"
          className="tl-add"
          title={t('timeline.AddResource')}
          data-automation-id="timeline-add-resource"
          onClick={(event) => {
            event.stopPropagation();
            setPicker({ anchorEl: event.currentTarget as HTMLElement, mode: 'add' });
          }}
          sx={{
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'var(--border-width-thin) dashed var(--border-subtle)',
            borderRadius: '50%',
            bgcolor: 'var(--bg-panel)',
            color: 'var(--fg-3)',
            cursor: 'pointer',
            p: 0,
            ml: shown.length > 0 || overflow > 0 ? '3px' : 0,
            '&:hover': { borderColor: 'var(--color-pink-80)', color: 'var(--color-pink-80)' },
          }}
        >
          <MaterialSymbol name="add" size={13} />
        </Box>
      </Box>

      {picker ? (
        <RolePickerPopover
          anchorEl={picker.anchorEl}
          mode={picker.mode}
          roleOptions={roleOptions}
          pricing={pricing}
          notice={catalogNotice}
          lookupsEnabled={lookupsEnabled}
          assignRole={assignTarget?.roleRef}
          onAdd={(role, person) => {
            setPicker(null);
            setAllocations(
              withAppendedAllocation(node, {
                id: crypto.randomUUID(),
                roleRef: role,
                personRef: person,
                hours: 0,
              }),
            );
          }}
          onAssign={(person) => {
            setPicker(null);
            if (picker.mode !== 'assign') return;
            setAllocations(
              node.allocations.map((allocation) =>
                allocation.id === picker.allocationId
                  ? { ...allocation, personRef: person }
                  : allocation,
              ),
            );
          }}
          onEditResources={openResources}
          onRemove={() => {
            setPicker(null);
            if (picker.mode !== 'assign') return;
            setAllocations(
              node.allocations.filter((allocation) => allocation.id !== picker.allocationId),
            );
          }}
          onClose={() => setPicker(null)}
        />
      ) : null}

      {/*
        The same popover the list row renders, on the same editor state — the
        views never coexist (the switcher swaps them), so one open node id is
        unambiguous.
      */}
      <ResourcesPopover node={node} anchorEl={stackEl} />
    </>
  );
};
